/** One circuit, as a card on the public map.
 *
 * The card is a progress report rather than an index entry, which is why the
 * outline sits between the name and the numbers: what a reader wants to know
 * is how much of this circuit exists yet, and the drawing answers that before
 * the percentages do. The two coverage bars are per side because the sides are
 * surveyed independently — one lap hugging the left kerb leaves the right
 * border untouched — and a single averaged figure would hide exactly the gap
 * somebody could go and fill.
 *
 * The circuit's name opens its own page — the map at a size worth reading,
 * and who surveyed it. The pull request that published the geometry lives on
 * GitHub and keeps its own link in the footer; a card with no pull request
 * says so with a dash rather than an inert link.
 *
 * The outline is drawn by the shared `TrackOutline`, which falls back to the
 * design's stylised loop when there is no compiled geometry. The list
 * endpoint sends none, so a card wears the fallback until the page has asked
 * `GET /v1/tracks/{id}/outline` for it, and keeps the fallback for a circuit
 * that has nothing published. The stroke follows the circuit's state either
 * way, so the grid reads correctly at a glance.
 */

import { Link } from 'react-router-dom';

import { Bar, Card, CardMeta, CardTitle, Tag, trackStateTone } from '@/components/ui';
import { TrackOutline } from '@/components/TrackOutline';
import { count, metres, percent } from '@/lib/format';
import { publicationLabel, pullRequestLabel } from '@/components/tracks/list';
import type { Track } from '@/lib/types';

/** `strokeWidth × 8` is what the fallback loop draws at, and 16 is the
 *  weight the design gives it. */
const PLACEHOLDER_WEIGHT = 2;

interface CoverageRowProps {
  label: string;
  pct: number | null;
}

function CoverageRow({ label, pct }: CoverageRowProps) {
  return (
    <>
      <span style={{ color: 'var(--color-neutral-500)' }}>{label}</span>
      <Bar value={pct ?? 0} />
      <span>{percent(pct)}</span>
    </>
  );
}

export interface TrackCardProps {
  track: Track;
}

export function TrackCard({ track }: TrackCardProps) {
  const pr = pullRequestLabel(track.last_pr_url);

  return (
    <Card elevation="sm" as="article" style={{ gap: 10 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 10,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <CardTitle as="h2" truncate>
            <Link
              to={`/tracks/${encodeURIComponent(track.official_id)}`}
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              {track.name}
            </Link>
          </CardTitle>
          <CardMeta className="tabular-nums">
            {track.official_id} · {metres(track.length_m)}
          </CardMeta>
        </div>
        <Tag tone={trackStateTone(track.status)} className="shrink-0">
          {track.status}
        </Tag>
      </div>

      <TrackOutline
        geometry={track.geometry}
        state={track.status}
        strokeWidth={PLACEHOLDER_WEIGHT}
        height={96}
      />

      <div
        className="tabular-nums"
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr) auto',
          gap: '4px 10px',
          alignItems: 'center',
          fontSize: 11.5,
        }}
      >
        <CoverageRow label="left" pct={track.coverage.left} />
        <CoverageRow label="right" pct={track.coverage.right} />
      </div>

      <div
        className="tabular-nums"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 6,
          fontSize: 11.5,
          color: 'var(--color-neutral-500)',
        }}
      >
        <span style={{ whiteSpace: 'nowrap' }}>
          {count(track.contributors)} contributors · {count(track.runs)} runs
        </span>
        <span style={{ whiteSpace: 'nowrap' }}>{count(track.pending)} pending</span>
      </div>

      <CardMeta className="justify-between">
        <span>{publicationLabel(track)}</span>
        {pr ? (
          <a
            href={track.last_pr_url}
            target="_blank"
            rel="noreferrer noopener"
            style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            {pr}
            <span className="sr-only"> for {track.name}, on GitHub</span>
          </a>
        ) : (
          <span aria-hidden="true">—</span>
        )}
      </CardMeta>
    </Card>
  );
}

export default TrackCard;
